import { useCallback, useEffect, useRef } from "react";

type DebouncedCallback<TArgs extends unknown[]> = (...args: TArgs) => void;

export const useDebouncedCallback = <TArgs extends unknown[]>(
  callback: DebouncedCallback<TArgs>,
  delay: number,
  throttle = false
): DebouncedCallback<TArgs> => {
  const timeoutRef = useRef<number>();
  const firstTimeTimeoutRef = useRef<number>();
  const isFirstTimeCalled = useRef(false);

  useEffect(
    () => () => {
      if (typeof timeoutRef.current !== "undefined") {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return useCallback(
    (...args) => {
      if (typeof timeoutRef.current !== "undefined") {
        if (!throttle) {
          window.clearTimeout(timeoutRef.current);
        } else {
          return;
        }
      }

      if (throttle && !isFirstTimeCalled.current) {
        isFirstTimeCalled.current = true;
        firstTimeTimeoutRef.current = window.setTimeout(() => {
          isFirstTimeCalled.current = false;
          firstTimeTimeoutRef.current = undefined;
        }, delay);
        callback(...args);
        return;
      }

      if (firstTimeTimeoutRef.current) {
        window.clearTimeout(firstTimeTimeoutRef.current);
        firstTimeTimeoutRef.current = undefined;
      }

      timeoutRef.current = window.setTimeout(() => {
        if (!throttle) {
          callback(...args);
        }
        isFirstTimeCalled.current = false;
        timeoutRef.current = undefined;
      }, delay);
    },
    [callback, delay, throttle]
  );
};
